import { createFileRoute, Link } from "@tanstack/react-router";
import { useCurrentUser } from "~/lib/hooks/useCurrentUser";

export const Route = createFileRoute("/flirtmachine/_authenticated/dashboard")({
  component: Dashboard,
});

function Dashboard() {
  const { error, isLoading, user } = useCurrentUser();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-red-50 text-red-800 p-4 rounded-lg">
          <h2 className="text-lg font-semibold mb-2">Error loading user</h2>
          <p className="text-sm">{String(error)}</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">No user found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Welcome Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Welcome back, {user.name}!
          </h1>
          <p className="text-gray-600">
            Here's an overview of your account
          </p>
        </div>

        {/* User Info Card */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center gap-6">
            {user.imageUrl && (
              <img
                src={user.imageUrl}
                alt={user.name}
                className="w-20 h-20 rounded-full object-cover"
              />
            )}
            {!user.imageUrl && (
              <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center">
                <span className="text-2xl text-gray-600">
                  {user.name.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-gray-900 mb-1">
                {user.name}
              </h2>
              <p className="text-gray-600 mb-3">{user.email}</p>
              <div className="flex gap-2">
                {user.roles.map((role) => (
                  <span
                    key={role}
                    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                      role === "admin"
                        ? "bg-purple-100 text-purple-800"
                        : role === "moderator"
                        ? "bg-blue-100 text-blue-800"
                        : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {role}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Links */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Quick Links
          </h3>
          <div className="space-y-3">
            <Link
              to="/flirtmachine"
              className="block px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
            >
              ← Back to Home
            </Link>
            <Link
              to="/flirtmachine/research/v2/new"
              className="block px-4 py-2 text-blue-700 hover:bg-blue-50 rounded-md transition-colors font-medium"
            >
              Research Assistant (V2 Beta) →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
